import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildRowColumnKeys,
  isRole23Empty,
  isRole23ValidatingControl,
  pickRole23Schema,
  Role23Dimension,
  Role23Schema,
} from './role23-notes-issued-capital.rules';
import {
  activeDimensions,
  readSlice,
  resolveDimensionVisibility,
} from './dimension-visibility.util';
import { getDimensionVisibilityConfig } from './dimension-visibility-registry';
import { getSchemaMin, valueBelowSchemaMin } from '../../../utils/schema-min.util';

/**
 * Pure validator for Role 23 (Notes - Issued Capital).
 *
 * Mirrors the editor's role23-740000-notes-issued-capital.component.ts
 * evaluateSheetStatus() surface:
 *
 *  1. Non-table required TextBlock fields (control.required === true AND
 *     NOT control.ui.tableColumn) — flagged when the corresponding flat
 *     sheetData key is empty. The component checks formControl.invalid;
 *     that invalidity is driven solely by Validators.required for TextBlock
 *     fields in this role (no pattern/min/max on TextBlock controls).
 *  2. Table-row required columns (control.required === true AND
 *     control.ui.tableColumn) — for each validating row, each of the four
 *     CurrentYear columns (Total + 3 share-class members) is flagged empty.
 *     The dimension axis (Consolidated / Separate) is toggled by the
 *     NatureOfFinancialStatements field, resolved via the shared
 *     resolveDimensionVisibility() resolver (fed by
 *     getDimensionVisibilityConfig()) which prefers `filingPrerequisites`
 *     and falls back to `filingInfo` (the backend payload that carries the
 *     same DEI-namespace field on `role-1.form.json:279`). When the filing
 *     is not Consolidated, only Separate is checked — matching the
 *     component's showConsolidatedTable gate. The filingInfo fallback is the
 *     Bug A fix: without it, a missing/late filingPrerequisites slice would
 *     silently default to "Consolidated visible" on Separate filings and
 *     over-report every Consolidated cell across all four members.
 *
 * ---- Schema variant handling --------------------------------------------
 *
 * Role 23 ships TWO form JSONs (MFRS / MPERS). The component picks at mount
 * time from the filingPrerequisites accounting-standard field; the pure
 * path gets the same value through context.accountingStandard (assembled by
 * ConversionValidatorService.buildContext from filingInfo). Selection is
 * delegated to pickRole23Schema so component and validator stay DRY.
 *
 * ---- Table-row repetition -----------------------------------------------
 *
 * Data shape: FLAT sheetData keyed by
 *   {qname}[{dim}][CurrentYear|PreviousYear]
 * or
 *   {qname}:{Member}[{dim}][CurrentYear|PreviousYear]
 *
 * NOT an array-of-rows. The validator expands each schema row's qname into
 * the column key list via buildRowColumnKeys() and probes the flat map.
 * This means no interface change to SheetValidator/SheetValidationContext
 * is required for role 23.
 *
 * ---- PreviousYear parity note -------------------------------------------
 *
 * The component only checks CurrentYear cells in getRowValidationStatus(),
 * even though PreviousYear required FormControls exist. The pure validator
 * intentionally mirrors that to stay parity-safe. See the "Latent bug
 * candidate" note in role23-notes-issued-capital.rules.ts.
 *
 * ---- min validators -----------------------------------------------------
 *
 * The schema declares several `validators: [{ name: 'min', value: 0 }]`
 * entries on the integer share-count columns. These ARE enforced: a present
 * value below the declared minimum (i.e. a negative share count) blocks the
 * sheet. The mounted editor mirrors this — its getValidators() re-attaches
 * Validators.min(declaredMin) for table columns and its rowHasBlockingError()
 * treats a present-but-below-min cell as a blocking error (independent of
 * dirty/touched), so this headless verdict and the editor's display verdict
 * stay in lockstep (role23 is on the PURE_VERDICT_SHEETS allow-list). Empty
 * cells are NOT a min violation — emptiness is handled by the required check
 * above; min only constrains a value that is actually present. Mirrors the
 * CurrentYear-only column set via buildRowColumnKeys() like every other
 * role23 check.
 *
 * ---- Historical note: skip-list removed ---------------------------------
 *
 * An earlier revision carried a `ROLE23_CALCULATED_ROW_QNAMES` skip-list for
 * NumberOfSharesOutstanding:End / AmountOfSharesOutstanding:End /
 * AmountOfOutstandingSharesIssuedDuringPeriod /
 * OtherChangesInAmountOfSharesOutstanding (and their MFRS/MPERS prefix
 * twins) on the assumption that the editor populated those rows on mount.
 * It does not — `triggerAllCalculations()` is a no-op (component:
 * "REMOVED: setupCalculations() — Total fields are now user-editable"), and
 * there is no other mount-time write path for these qnames. The skip-list
 * was masking legitimate empty-required-field errors and inverting the
 * parity contract (pure became less strict than mount-based). It has been
 * removed; if the original "editor flips to Validated despite missing data"
 * symptom returns it must be diagnosed in the component, not papered over
 * here.
 */

function checkNonTableRequiredErrors(
  schema: Role23Schema,
  sheetData: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of schema.controls) {
    if (!isRole23ValidatingControl(control)) continue;
    if (control.ui?.tableColumn) continue; // handled separately
    if (control.required !== true) continue;
    if (isRole23Empty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }
  return errors;
}

function checkTableRowRequiredErrors(
  schema: Role23Schema,
  sheetData: Record<string, any>,
  dimensions: ReadonlyArray<Role23Dimension>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of schema.controls) {
    if (!isRole23ValidatingControl(control)) continue;
    if (!control.ui?.tableColumn) continue;
    if (control.required !== true) continue;

    for (const dimension of dimensions) {
      for (const key of buildRowColumnKeys(control.qname, dimension)) {
        if (isRole23Empty(sheetData[key])) {
          errors[key] = `${key} is required`;
        }
      }
    }
  }
  return errors;
}

/**
 * Flag any min-constrained table cell whose present value is below its declared
 * minimum (i.e. a negative share count). Mirrors the editor's re-enforced
 * Validators.min(declaredMin) + rowHasBlockingError min check. Only CurrentYear
 * cells of active dimensions are probed (via buildRowColumnKeys), exactly like
 * the required check. Empty cells are skipped — emptiness is the required
 * check's job; min only constrains a value that is actually present.
 */
function checkTableRowMinErrors(
  schema: Role23Schema,
  sheetData: Record<string, any>,
  dimensions: ReadonlyArray<Role23Dimension>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of schema.controls) {
    if (!isRole23ValidatingControl(control)) continue;
    if (!control.ui?.tableColumn) continue;
    const min = getSchemaMin(control);
    if (min === undefined) continue;

    for (const dimension of dimensions) {
      for (const key of buildRowColumnKeys(control.qname, dimension)) {
        if (valueBelowSchemaMin(control, sheetData[key])) {
          errors[key] = `${key} must be at least ${min}`;
        }
      }
    }
  }
  return errors;
}

/**
 * The dimensions the editor actually renders & validates for this filing —
 * both when Consolidated is visible, Separate-only when it is hidden.
 *
 * Delegates to the shared `resolveDimensionVisibility()` resolver (fed by the
 * central `getDimensionVisibilityConfig()` registry) so this headless path and
 * the mounted editor's `CrossReferenceService.getVisibility()` derive the gate
 * identically and cannot drift. The resolver applies the same prereq-first,
 * then-filingInfo precedence the role-local helper used to: it prefers the
 * `filingPrerequisites` slice (editor-side source of truth) and falls back to
 * `filingInfo`; an unknown/unset nature defaults to Consolidated visible — the
 * editor's pre-subscription posture. Role 23 has no period axis (CurrentYear
 * only for validation), so `activeDimensions()` collapses to ['Separate'] for a
 * Separate filing and ['Consolidated', 'Separate'] otherwise.
 */
function resolveActiveDimensions(
  context: SheetValidationContext
): ReadonlyArray<Role23Dimension> {
  const cells = resolveDimensionVisibility(
    getDimensionVisibilityConfig(SHEET_KEYS.notesIssuedCapital)!,
    {
      filingPrerequisites: readSlice(context.crossSheetData, 'filingPrerequisites'),
      filingInfo: readSlice(context.crossSheetData, 'filingInfo'),
    }
  );
  return activeDimensions(cells) as ReadonlyArray<Role23Dimension>;
}

export const role23NotesIssuedCapitalValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.notesIssuedCapital,
  // Tier 2: requiredIf/hideWhen against a cross-sheet field
  // (filingPrerequisites.NatureOfFinancialStatements toggles the
  // Consolidated dimension) combined with same-sheet virtual table-row
  // expansion. No calculations, no true row-array repetition, so it stays
  // below Tier 4.
  tier: 2,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole23Schema(context.accountingStandard);
    const dimensions = resolveActiveDimensions(context);

    const errors: Record<string, string> = {
      ...checkNonTableRequiredErrors(schema, sheetData),
      ...checkTableRowRequiredErrors(schema, sheetData, dimensions),
      ...checkTableRowMinErrors(schema, sheetData, dimensions),
    };

    const result = {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: false,
      errors,
    };
    // ------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('[On Role 23 Notes - Issued Capital | Validation]: Schema: ', schema);
    // console.log('[On Role 23 Notes - Issued Capital | Validation]: Sheet Data: ', sheetData);
    // console.log('[On Role 23 Notes - Issued Capital | Validation]: Dimensions: ', dimensions);
    // console.log('[On Role 23 Notes - Issued Capital | Validation]: Errors: ', errors);
    // console.log('[On Role 23 Notes - Issued Capital | Validation]: Result: ', result);
    // ------------------
    return result;
  },
};
